import { useEffect, useState, useCallback } from "react";
import { Loader2, Mail, RefreshCw, RotateCw, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const KIND_LABEL = {
  new_request: "New request",
  status_change: "Status change",
  admin_message: "Admin message",
  retention_digest: "Retention digest",
};

export default function EmailAuditPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL"); // ALL | failed | sent
  const [retrying, setRetrying] = useState(null); // id of row

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (filter !== "ALL") params.status = filter;
      const { data } = await api.get("/admin/email-sends", { params });
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const resend = async (id) => {
    setRetrying(id);
    try {
      const { data } = await api.post(`/admin/email-sends/${id}/resend`);
      toast[data.status === "sent" ? "success" : "error"](
        data.status === "sent"
          ? "Resent successfully"
          : `Retry ended as "${data.status}"${data.error ? ` — ${String(data.error).slice(0, 120)}` : ""}`
      );
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRetrying(null);
    }
  };

  const chip = (s) => {
    if (s === "sent")
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold border bg-emerald-500/10 border-emerald-500/30 text-emerald-400">
          <CheckCircle2 className="w-3 h-3" /> Sent
        </span>
      );
    if (s === "failed" || s === "error" || s === "guardrail_blocked")
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold border bg-red-500/10 border-red-500/30 text-red-300">
          <XCircle className="w-3 h-3" /> {s}
        </span>
      );
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold border bg-amber-500/10 border-amber-500/30 text-amber-400">
        <AlertCircle className="w-3 h-3" /> {s}
      </span>
    );
  };

  return (
    <div
      className="bg-[#18181E] border border-white/10 rounded-2xl p-5 sm:p-6"
      data-testid="email-audit-panel"
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-400">
            / Outbound Emails
          </div>
          <h3 className="font-display text-lg font-bold uppercase tracking-wider mt-1 flex items-center gap-2">
            <Mail className="w-4 h-4 text-amber-400" /> Email Audit
          </h3>
        </div>
        <button
          onClick={load}
          data-testid="email-audit-refresh-btn"
          className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white px-3 py-2 rounded-lg border border-white/10 text-xs transition"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {["ALL", "sent", "failed", "error"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`email-filter-${s.toLowerCase()}`}
            className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-semibold transition ${
              filter === s
                ? "bg-amber-500 text-black border border-amber-500"
                : "bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="text-center text-sm text-slate-500 py-10">No emails yet.</div>
      )}

      {!loading && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="email-audit-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/5">
                <th className="py-2 pr-3 font-semibold">When</th>
                <th className="py-2 pr-3 font-semibold">Kind</th>
                <th className="py-2 pr-3 font-semibold">To</th>
                <th className="py-2 pr-3 font-semibold">Request</th>
                <th className="py-2 pr-3 font-semibold">Status</th>
                <th className="py-2 pr-3 font-semibold text-right">Retries</th>
                <th className="py-2 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const kind = r.context?.kind;
                const rid = r.context?.request_id;
                const canRetry =
                  r.status === "failed" || r.status === "error" || r.status === "skipped";
                return (
                  <tr
                    key={r.id}
                    data-testid={`email-row-${r.id}`}
                    className="border-b border-white/5 hover:bg-white/[0.02]"
                  >
                    <td className="py-2.5 pr-3 text-slate-400 text-xs">
                      {new Date(r.at).toLocaleString()}
                    </td>
                    <td className="py-2.5 pr-3 text-slate-200">
                      {KIND_LABEL[kind] || kind || "—"}
                    </td>
                    <td className="py-2.5 pr-3 text-slate-300 text-xs">{r.to}</td>
                    <td className="py-2.5 pr-3 font-mono-brand text-amber-400 text-xs">
                      {rid || "—"}
                    </td>
                    <td className="py-2.5 pr-3">{chip(r.status)}</td>
                    <td className="py-2.5 pr-3 text-right text-slate-400 text-xs">
                      {r.retries || 0}
                    </td>
                    <td className="py-2.5 text-right">
                      {canRetry && (
                        <button
                          onClick={() => resend(r.id)}
                          disabled={retrying === r.id}
                          data-testid={`email-resend-btn-${r.id}`}
                          className="inline-flex items-center gap-1 bg-amber-500/15 hover:bg-amber-500/25 disabled:opacity-50 text-amber-300 border border-amber-500/30 rounded-lg px-2.5 py-1.5 text-[11px] uppercase tracking-widest font-semibold transition"
                        >
                          {retrying === r.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <RotateCw className="w-3 h-3" />
                          )}
                          Resend
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
